// Thème white-label piloté par l'établissement résolu.
// Aucune couleur TED en dur : défauts neutres KS Tech.
export const DEFAULT_ACCENT = '#111827';      // graphite neutre (remplace l ancien jaune par defaut)
export const DEFAULT_SECONDAIRE = '#6B7280';  // gris neutre

function normalizeHex(hex) {
  if (!hex || typeof hex !== 'string') return null;
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) h = h.split('').map((x) => x + x).join('');
  return /^[0-9a-fA-F]{6}$/.test(h) ? h.toLowerCase() : null;
}

// Renvoie le texte (#111827 ou #ffffff) le plus contrasté sur `hex` (WCAG, luminance relative).
export function contrastText(hex) {
  const c = normalizeHex(hex);
  if (!c) return '#111827';
  const chan = (i) => {
    const v = parseInt(c.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  const L = 0.2126 * chan(0) + 0.7152 * chan(2) + 0.0722 * chan(4);
  const contrastWhite = 1.05 / (L + 0.05);
  const contrastBlack = (L + 0.05) / 0.05;
  return contrastBlack >= contrastWhite ? '#111827' : '#ffffff';
}

// Extrait une palette (accent + secondaire) d'une image via canvas — SUGGESTION only,
// aucune dépendance externe. Le client peut toujours override.
export function extractPalette(imgEl) {
  try {
    const canvas = document.createElement('canvas');
    const w = (canvas.width = 48), h = (canvas.height = 48);
    const ctx = canvas.getContext('2d');
    ctx.drawImage(imgEl, 0, 0, w, h);
    const { data } = ctx.getImageData(0, 0, w, h);
    const buckets = {};
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] < 128) continue; // transparent
      const r = data[i], g = data[i + 1], b = data[i + 2];
      const max = Math.max(r, g, b), min = Math.min(r, g, b);
      if (max > 242 && min > 242) continue; // quasi-blanc (fond)
      if (max < 16) continue;               // quasi-noir
      const key = `${r >> 4},${g >> 4},${b >> 4}`;
      const bk = buckets[key] || (buckets[key] = { r: 0, g: 0, b: 0, n: 0, sat: 0 });
      bk.r += r; bk.g += g; bk.b += b; bk.n += 1; bk.sat += max - min;
    }
    const arr = Object.values(buckets).map((b) => ({
      r: Math.round(b.r / b.n), g: Math.round(b.g / b.n), b: Math.round(b.b / b.n), n: b.n, sat: b.sat / b.n,
    }));
    if (!arr.length) return null;
    const byScore = [...arr].sort((x, y) => y.sat * y.n - x.sat * x.n);
    const accent = byScore[0];
    const secondaire = byScore.find((c) => Math.abs(c.r - accent.r) + Math.abs(c.g - accent.g) + Math.abs(c.b - accent.b) > 80) || byScore[1] || accent;
    const hex = (c) => '#' + [c.r, c.g, c.b].map((v) => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0')).join('');
    return { accent: hex(accent), secondaire: hex(secondaire) };
  } catch { return null; }
}

// ─── Contraste en premier plan (accent utilisé comme texte/icône/bordure) ────────
// On ajuste UNIQUEMENT la clarté (HSL) en gardant teinte + saturation → l'identité de
// marque reste reconnaissable (un vert reste vert), avec un plancher WCAG AA (4.5:1).
export const DARK_SURFACE = '#111111';   // sidebar & en-têtes sombres du CRM
export const LIGHT_SURFACE = '#ffffff';  // contenu clair

function hexToRgb(hex) {
  const c = normalizeHex(hex);
  return c ? { r: parseInt(c.slice(0, 2), 16), g: parseInt(c.slice(2, 4), 16), b: parseInt(c.slice(4, 6), 16) } : null;
}
function rgbToHex({ r, g, b }) {
  return '#' + [r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');
}
function relLum({ r, g, b }) {
  const f = (v) => { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}
function contrastRatio(a, b) {
  const hi = Math.max(relLum(a), relLum(b)), lo = Math.min(relLum(a), relLum(b));
  return (hi + 0.05) / (lo + 0.05);
}
function rgbToHsl({ r, g, b }) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s; const l = (max + min) / 2;
  if (max === min) { h = 0; s = 0; }
  else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return { h, s, l };
}
function hslToRgb({ h, s, l }) {
  if (s === 0) return { r: l * 255, g: l * 255, b: l * 255 };
  const hue2 = (p, q, t) => { if (t < 0) t += 1; if (t > 1) t -= 1; if (t < 1 / 6) return p + (q - p) * 6 * t; if (t < 1 / 2) return q; if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6; return p; };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return { r: hue2(p, q, h + 1 / 3) * 255, g: hue2(p, q, h) * 255, b: hue2(p, q, h - 1 / 3) * 255 };
}
// Ajuste la clarté (garde H,S) jusqu'à contraste >= min sur `surface`. mode: 'lighten' | 'darken'.
function ensureReadable(hex, surfaceHex, min, mode) {
  const rgb0 = hexToRgb(hex); if (!rgb0) return hex;
  const surf = hexToRgb(surfaceHex);
  if (contrastRatio(rgb0, surf) >= min) return rgbToHex(rgb0);
  const { h, s } = rgbToHsl(rgb0);
  let l = rgbToHsl(rgb0).l;
  const step = mode === 'lighten' ? 0.02 : -0.02;
  for (let i = 0; i < 60 && l >= 0 && l <= 1; i++) {
    l += step;
    const rgb = hslToRgb({ h, s, l: Math.max(0, Math.min(1, l)) });
    if (contrastRatio(rgb, surf) >= min) return rgbToHex(rgb);
  }
  return rgbToHex(hslToRgb({ h, s, l: Math.max(0, Math.min(1, l)) }));
}
export function accentOnDark(hex) { return ensureReadable(hex, DARK_SURFACE, 4.5, 'lighten'); }
export function accentOnLight(hex) { return ensureReadable(hex, LIGHT_SURFACE, 4.5, 'darken'); }

// Applique le thème de l'établissement sur :root via variables CSS.
export function applyTheme(etab) {
  const accent = (etab && etab.couleur_accent) || DEFAULT_ACCENT;
  const secondaire = (etab && etab.couleur_secondaire) || DEFAULT_SECONDAIRE;
  const root = document.documentElement;
  root.style.setProperty('--accent', accent);                       // fond plein
  root.style.setProperty('--on-accent', contrastText(accent));      // texte SUR un fond accent
  root.style.setProperty('--accent-on-dark', accentOnDark(accent)); // accent en 1er plan sur surface sombre
  root.style.setProperty('--accent-on-light', accentOnLight(accent));// accent en 1er plan sur surface claire
  root.style.setProperty('--secondaire', secondaire);
  root.style.setProperty('--on-secondaire', contrastText(secondaire));
  root.style.setProperty('--secondaire-on-dark', accentOnDark(secondaire));
  root.style.setProperty('--secondaire-on-light', accentOnLight(secondaire));
}
